import { Col, Row } from "antd";
import { useContext, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { INITIAL_PAGE } from "../../../../utils/helpers/constants";
import FieldBuilder from "../../../form-components/field-builder";
import Form from "../../../form-components/from";
import ShelfContext from "../../../../context/wms/shelf/context";
import { AisleLayout, PlacementSide } from "../../../../models/wms/enums";
import useHierarchicalDropdown from "../../../../hooks/wms/use-hierarchical-dropdown";
import FilterCascadeReset from "../../shared/filter-cascade-reset";

const ShelfFilter: React.FC = () => {
  const { t } = useTranslation();
  const { actions, query } = useContext(ShelfContext);

  // Use hierarchical dropdown hook with progressive visibility
  const {
    stores,
    loadingStores,
    zoneTypes,
    loadingZoneTypes,
    zones,
    loadingZones,
    aisles,
    loadingAisles,
    storageUnits,
    loadingStorageUnits,
    visibleLevels,
    selectedStoreId,
    selectedZoneTypeId,
    selectedZoneId,
    selectedAisleId,
    selectedAisleLayout,
    selectedStorageUnitId,
    selectedSidesFilter,
    selectedSideFilter,
    setSelectedStoreId,
    setSelectedZoneTypeId,
    setSelectedZoneId,
    setSelectedAisleId,
    setSelectedStorageUnitId,
    setSelectedSidesFilter,
    setSelectedSideFilter,
  } = useHierarchicalDropdown({ debounceMs: 300, shelvesWithBinsOnly: false });

  // Derive valid PlacementSide options from the selected aisle's layout
  const sideOptions = useMemo(() => {
    const allOptions = [
      { label: t("right_side"), value: PlacementSide.R },
      { label: t("left_side"), value: PlacementSide.L },
    ];
    // If a specific aisle is selected, use its layout to filter side options
    const layoutToUse = selectedAisleLayout;

    if (!layoutToUse) return allOptions;
    if (layoutToUse === AisleLayout.LEFT) return [{ label: t("left_side"), value: PlacementSide.L }];
    if (layoutToUse === AisleLayout.RIGHT) return [{ label: t("right_side"), value: PlacementSide.R }];
    return allOptions; // BOTH
  }, [selectedAisleLayout, t]);

  return (
    <Form
      formId="shelf-filter"
      onSubmit={(data) => {
        const filter: any = {
          page: INITIAL_PAGE,
          perPage: query?.perPage,
          keyword: query?.keyword,
          filters: [
            selectedStoreId
              ? { name: "storeId", value: selectedStoreId, operation: "eq" }
              : undefined,
            selectedZoneId
              ? { name: "zoneId", value: selectedZoneId, operation: "eq" }
              : undefined,
            selectedAisleId
              ? { name: "aisleId", value: selectedAisleId, operation: "eq" }
              : undefined,
            selectedStorageUnitId
              ? {
                  name: "storageUnitId",
                  value: selectedStorageUnitId,
                  operation: "eq",
                }
              : undefined,
            data?.hasBins !== undefined && data?.hasBins !== null
              ? { name: "hasBins", value: data.hasBins, operation: "eq" }
              : undefined,
            data?.isActive !== undefined && data?.isActive !== null
              ? { name: "isActive", value: data.isActive, operation: "eq" }
              : undefined,
          ].filter((f) => f),
        };
        actions.setQuery(filter);
      }}
    >
      <FilterCascadeReset
        rules={[
          {
            watch: selectedStoreId,
            clear: ["zoneId", "aisleId", "storageUnitId", "side", "sides", "zoneTypeId"],
            resetSetters: [setSelectedZoneId, setSelectedAisleId, setSelectedStorageUnitId, setSelectedSideFilter, setSelectedSidesFilter, setSelectedZoneTypeId],
          },
          {
            watch: selectedZoneId,
            clear: ["aisleId", "storageUnitId", "side", "sides"],
            resetSetters: [setSelectedAisleId, setSelectedStorageUnitId, setSelectedSideFilter, setSelectedSidesFilter],
          },
          {
            watch: selectedAisleId,
            clear: ["storageUnitId", "side"],
            resetSetters: [setSelectedStorageUnitId, setSelectedSideFilter],
          },
        ]}
      />
      <Row justify={"start"} gutter={[10, 0]}>
        <Col xs={24} sm={12} md={8}>
          <FieldBuilder
            name="storeId"
            label={`${t("select_store")}`}
            width="large"
            rules={{ required: false }}
            input={{
              type: "select",
              loading: loadingStores,
              allowSearch: true,
              options: stores.map((s) => ({
                label: s.label,
                value: s.id,
              })),
              onChange: setSelectedStoreId,
            }}
          />
        </Col>
        {visibleLevels.zone && (
          <Col xs={24} sm={12} md={8}>
            <FieldBuilder
              name="zoneTypeId"
              label={t("zone_type")}
              width="large"
              rules={{ required: false }}
              input={{
                type: "select",
                loading: loadingZoneTypes,
                allowSearch: true,
                options: zoneTypes.map((zt) => ({
                  label: zt.label,
                  value: zt.id,
                })),
                onChange: setSelectedZoneTypeId,
              }}
            />
          </Col>
        )}
        {visibleLevels.zone && (
          <Col xs={24} sm={12} md={8}>
            <FieldBuilder
              name="zoneId"
              label={`${t("select_zone")}`}
              width="large"
              rules={{ required: false }}
              input={{
                type: "select",
                loading: loadingZones,
                allowSearch: true,
                options: zones.map((z) => ({
                  label: z.label,
                  value: z.id,
                })),
                onChange: setSelectedZoneId,
              }}
            />
          </Col>
        )}

        {visibleLevels.aisle && (
          <Col xs={24} sm={12} md={8}>
            <FieldBuilder
              name="sides"
              label={t("available_sides")}
              width="large"
              rules={{ required: false }}
              input={{
                type: "select",
                options: [
                  { label: t("left_side"), value: AisleLayout.LEFT },
                  { label: t("right_side"), value: AisleLayout.RIGHT },
                  { label: t("both_sides"), value: AisleLayout.BOTH },
                ],
                onChange: setSelectedSidesFilter,
              }}
            />
          </Col>
        )}

        {visibleLevels.aisle && (
          <Col xs={24} sm={12} md={8}>
            <FieldBuilder
              name="aisleId"
              label={`${t("select_aisle")}`}
              width="large"
              rules={{ required: false }}
              input={{
                type: "select",
                loading: loadingAisles,
                allowSearch: true,
                options: aisles.map((a) => ({
                  label: a.label,
                  value: a.id,
                })),
                onChange: setSelectedAisleId,
              }}
            />
          </Col>
        )}

        {visibleLevels.storageUnit && (
          <Col xs={24} sm={12} md={8}>
            <FieldBuilder
              name="side"
              label={t("placement_side")}
              width="large"
              rules={{ required: false }}
              input={{
                type: "select",
                options: sideOptions,
                onChange: setSelectedSideFilter,
              }}
            />
          </Col>
        )}

        {visibleLevels.storageUnit && (
          <Col xs={24} sm={12} md={8}>
            <FieldBuilder
              name="storageUnitId"
              label={`${t("select_storage_unit")}`}
              width="large"
              rules={{ required: false }}
              input={{
                type: "select",
                loading: loadingStorageUnits,
                allowSearch: true,
                options: storageUnits.map((su) => ({
                  label: su.label,
                  value: su.id,
                })),
                onChange: setSelectedStorageUnitId,
              }}
            />
          </Col>
        )}
        <Col xs={24} sm={12} md={8}>
          <FieldBuilder
            name="hasBins"
            label={`${t("has_bins")}`}
            width="large"
            rules={{ required: false }}
            input={{
              type: "select",
              options: [
                { label: t("yes"), value: 1 },
                { label: t("no"), value: 0 },
              ],
            }}
          />
        </Col>
        <Col xs={24} sm={12} md={8}>
          <FieldBuilder
            name="isActive"
            label={`${t("is_active")}`}
            width="large"
            rules={{ required: false }}
            input={{
              type: "select",
              options: [
                { label: t("active"), value: 1 },
                { label: t("inactive"), value: 0 },
              ],
            }}
          />
        </Col>
      </Row>
    </Form>
  );
};

export default ShelfFilter;
